import type { Top5Category, Top5RankedIssue, Top5Widget } from "@broadview/api-client";
import { Link } from "@tanstack/react-router";
import { ArrowLeft, CircleDot, FileSearch, FolderOpen, Trophy } from "lucide-react";
import { useState } from "react";
import { formatRecordCost } from "@/lib/problem-record-presentation";
import { useTop5InputsQuery } from "@/lib/portal-queries";
import { PortalAccountMenu } from "./PortalAccountMenu";
import { PortalDataError, PortalLoadingState } from "./PortalDataState";

function formatImpact(issue: Top5RankedIssue): string | undefined {
  return issue.impact ? formatRecordCost(issue) : undefined;
}

function top5DataStateLabel(
  issue: Pick<Top5RankedIssue, "dataState" | "authorityClass" | "freshness">,
) {
  return issue.dataState === "SYNTHETIC" || issue.authorityClass === "DEMONSTRATION_ONLY"
    ? "Synthetic demonstration data"
    : issue.freshness === "STALE"
      ? "Historical real data"
      : issue.dataState === "LIVE"
        ? "Live governed data"
        : `${issue.dataState.toLowerCase()} governed data`;
}

function RankedIssueCard({ issue }: Readonly<{ issue: Top5RankedIssue }>) {
  const impact = formatImpact(issue);
  return (
    <article className="rounded-xl border border-border bg-card p-5">
      <div className="flex flex-wrap items-start gap-3">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/15 text-sm font-bold text-primary">
          {issue.rank}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="font-semibold">{issue.title}</h3>
            <span className="rounded-full border border-border bg-muted px-2 py-0.5 text-[11px]">
              {issue.signal.severity ?? issue.signal.status}
            </span>
          </div>
          {issue.description && (
            <p className="mt-1 text-sm text-muted-foreground">{issue.description}</p>
          )}
        </div>
      </div>

      <dl className="mt-4 grid gap-3 text-sm sm:grid-cols-3">
        <div>
          <dt className="text-xs text-muted-foreground">Ranking basis</dt>
          <dd className="mt-0.5 font-medium">
            {issue.rankingBasis.label}: {String(issue.rankingBasis.value)}
          </dd>
        </div>
        <div>
          <dt className="text-xs text-muted-foreground">Claim evidence</dt>
          <dd className="mt-0.5 font-medium">
            {issue.claimCount ?? "Unavailable"} claims{impact ? ` · ${impact}` : ""}
          </dd>
        </div>
        <div>
          <dt className="text-xs text-muted-foreground">Evidence state</dt>
          <dd className="mt-0.5 font-medium">
            {issue.freshness} · {issue.quality} · {issue.reconciliation}
          </dd>
        </div>
      </dl>

      <div className="mt-4 rounded-lg border border-border bg-background p-3 text-xs">
        <div className="flex flex-wrap items-center gap-2">
          <span className="inline-flex items-center gap-1 font-semibold text-primary">
            <CircleDot className="h-3 w-3" /> {top5DataStateLabel(issue)}
          </span>
          <span className="text-muted-foreground">Authority: {issue.authorityClass}</span>
        </div>
        <p className="mt-1 text-muted-foreground">
          {issue.sourceSummary} Data cutoff {new Date(issue.dataCutoffAt).toLocaleString()}.
        </p>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        {issue.problemRecord && (
          <Link
            to="/problem-record/$id"
            params={{ id: issue.problemRecord.id }}
            className="rounded-md bg-primary px-3 py-2 text-xs font-semibold text-primary-foreground"
          >
            Open {issue.problemRecord.displayId}
          </Link>
        )}
        {issue.evidence.map((evidence) => (
          <span
            key={evidence.id}
            className="rounded-md border border-border px-3 py-2 text-xs text-muted-foreground"
          >
            {evidence.title} · {evidence.classification}
          </span>
        ))}
      </div>
    </article>
  );
}

function Top5CategoryPanel({ widget }: Readonly<{ widget: Top5Widget }>) {
  if (widget.dataState === "UNAVAILABLE") {
    return (
      <section className="rounded-xl border border-dashed border-border bg-card p-8 text-center">
        <FileSearch className="mx-auto h-7 w-7 text-muted-foreground" />
        <h2 className="mt-3 font-semibold">{widget.payload.label} ranking is unavailable</h2>
        <p className="mx-auto mt-1 max-w-2xl text-sm text-muted-foreground">
          {widget.payload.unavailableReason}
        </p>
      </section>
    );
  }
  return (
    <section className="space-y-3" aria-label={`${widget.payload.label} rankings`}>
      {widget.payload.items.map((issue) => (
        <RankedIssueCard key={issue.id} issue={issue} />
      ))}
    </section>
  );
}

export function LiveTop5({
  initialCategory = "problems",
  embedded = false,
}: {
  initialCategory?: Top5Category;
  embedded?: boolean;
}) {
  const top5 = useTop5InputsQuery();
  const [category, setCategory] = useState<Top5Category>(initialCategory);
  const widgets = top5.data?.widgets ?? [];
  const active = widgets.find((widget) => widget.payload.category === category);

  const Main = embedded ? "div" : "main";
  return (
    <div className={embedded ? "space-y-4" : "min-h-screen bg-background text-foreground"}>
      {!embedded && (
        <header className="sticky top-0 z-30 border-b border-border bg-background/80 backdrop-blur-xl">
          <div className="mx-auto flex max-w-[1500px] flex-wrap items-center gap-3 px-6 py-4">
            <div className="flex items-center gap-2.5">
              <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary text-primary-foreground">
                <Trophy className="h-5 w-5" />
              </span>
              <div>
                <div className="text-sm font-semibold">Top 5 Intelligence</div>
                <div className="text-xs text-muted-foreground">
                  Governed management prioritization
                </div>
              </div>
            </div>
            <div className="ml-auto flex items-center gap-2">
              <Link
                to="/"
                className="inline-flex items-center gap-1.5 rounded-md border border-border bg-card px-3 py-1.5 text-xs font-medium hover:bg-muted"
              >
                <ArrowLeft className="h-3.5 w-3.5" /> Dashboard
              </Link>
              <Link
                to="/problem-records"
                className="inline-flex items-center gap-1.5 rounded-md border border-primary/40 bg-primary/10 px-3 py-1.5 text-xs font-medium text-primary"
              >
                <FolderOpen className="h-3.5 w-3.5" /> Problem Records
              </Link>
              <PortalAccountMenu />
            </div>
          </div>
        </header>
      )}
      <Main className={embedded ? "space-y-4" : "mx-auto max-w-[1500px] space-y-6 px-6 py-8"}>
        {!embedded && (
          <section>
            <h1 className="text-2xl font-semibold tracking-tight">Top 5 Intelligence</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Ranked canonical warranty signals with claim, Problem Record, and source provenance.
            </p>
          </section>
        )}

        {top5.isPending ? (
          <PortalLoadingState label="Loading governed Top 5 rankings" />
        ) : top5.isError ? (
          <PortalDataError
            error={top5.error}
            onRetry={() => void top5.refetch()}
            title="Top 5 rankings could not be loaded"
          />
        ) : (
          <>
            {!embedded && (
              <nav aria-label="Top 5 categories" className="flex flex-wrap gap-2">
                {widgets.map((widget) => (
                  <button
                    key={widget.widgetId}
                    type="button"
                    onClick={() => setCategory(widget.payload.category)}
                    className={`rounded-md border px-3 py-2 text-xs font-semibold ${
                      widget.payload.category === category
                        ? "border-primary bg-primary/15 text-primary"
                        : "border-border bg-card text-muted-foreground"
                    }`}
                  >
                    {widget.payload.label}
                    {widget.dataState === "UNAVAILABLE" ? " · unavailable" : ""}
                  </button>
                ))}
              </nav>
            )}
            {active && <Top5CategoryPanel widget={active} />}
          </>
        )}
      </Main>
    </div>
  );
}
